import * as React from "react";
import { toast } from "react-toastify";

import {
  IPromotion,
  usePromotionsApi,
  PromotionsRequestName
} from "src/modules/promotions/api/promotions.api";

// previousState added to access previous template state whilst editting promotion
export interface PromotionEditCreateState extends IPromotion {
  previousState?: PromotionEditCreateState;
}

export interface UsePromotionsReturnType {
  isLoading: boolean;
  promotion: PromotionEditCreateState;
  updatePromotionState: (udpatedPromotionState: Partial<PromotionEditCreateState>) => void;
}

/**
 * Use promotions hook
 */
const usePromotions = (initialState: PromotionEditCreateState, promotionId?: number): UsePromotionsReturnType => {
  const [ isLoading, setIsLoading ] = React.useState(false);
  const [ promotion, setPromotion ] = React.useState<PromotionEditCreateState>(initialState);
  const [ getPromotionRes, getPromotionReq ] = usePromotionsApi(PromotionsRequestName.GET_PROMOTION);

  // On mount, attempt to get the requested promotion
  React.useEffect(() => {
    // Get the given promotion if a promotionId is given
    if (promotionId) {
      // Update the isLoading state
      setIsLoading(true);
      getPromotionReq({ id: promotionId });
    }
  }, []);

  // On getPromotionRes change
  React.useEffect(() => {
    // If an error is returned
    if (getPromotionRes.error) {
      // Update the isLoading state
      setIsLoading(false);
      // Show the error message
      toast.error(getPromotionRes.error.message);
    }

    // On success
    if (getPromotionRes.data) {
      // Update the isLoading state
      setIsLoading(false);
      // Update the promotion state
      setPromotion(getPromotionRes.data);
    }
  }, [ getPromotionRes.data, getPromotionRes.error ]);

  // manages promotion state giving access to prev state
  const updatePromotionState = React.useCallback((udpatedPromotionState: Partial<PromotionEditCreateState>) => {
    setPromotion(prevState => ({
      ...prevState,
      previousState: prevState,
      ...udpatedPromotionState
    }));
  }, []);

  return {
    isLoading,
    promotion,
    updatePromotionState
  };
};

export default usePromotions;
